/**
 * Collection Filters & Sorting
 * AJAX filtering via Section Rendering API, History API, AbortController.
 */
(function () {
  'use strict';

  const SECTION_ID = document.querySelector('[data-collection-section-id]')?.dataset.collectionSectionId;
  if (!SECTION_ID) return;

  const selectors = {
    form: '#CollectionFiltersForm',
    sortSelect: '[data-sort-select]',
    filterInput: '[data-filter-input]',
    filterRemove: '[data-filter-remove]',
    productsContainer: '#CollectionProducts',
    productCount: '#CollectionProductCount',
    filtersContainer: '#CollectionFilters',
    activeFilters: '#ActiveFilters',
    paginationContainer: '#CollectionPagination',
    filterToggle: '[data-filter-toggle]',
    filterDrawer: '#FilterDrawer',
    filterOverlay: '#FilterOverlay',
    loadMoreBtn: '[data-load-more]'
  };

  let abortController = null;
  let debounceTimer = null;

  /* ================================
     INITIALIZATION
     ================================ */
  function init() {
    bindFilterInputs();
    bindSortSelect();
    bindFilterRemove();
    bindMobileToggle();
    bindLoadMore();
    bindPaginationClicks();
  }

  /* ================================
     EVENT BINDING
     ================================ */
  function bindFilterInputs() {
    document.querySelectorAll(selectors.filterInput).forEach(function (input) {
      var eventType = input.type === 'number' ? 'change' : 'change';
      input.addEventListener(eventType, function () {
        if (input.type === 'number') {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(function () { applyFilters(); }, 500);
        } else {
          applyFilters();
        }
      });
    });
  }

  function bindSortSelect() {
    var sortSelect = document.querySelector(selectors.sortSelect);
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        applyFilters();
      });
    }
  }

  function bindFilterRemove() {
    document.addEventListener('click', function (e) {
      var removeLink = e.target.closest(selectors.filterRemove);
      if (removeLink) {
        e.preventDefault();
        fetchAndRender(removeLink.href);
      }
    });
  }

  function bindMobileToggle() {
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest(selectors.filterToggle);
      if (toggle) {
        var drawer = document.querySelector(selectors.filterDrawer);
        var overlay = document.querySelector(selectors.filterOverlay);
        if (drawer) {
          var isOpen = drawer.classList.toggle('is-open');
          document.body.classList.toggle('no-scroll', isOpen);
          if (overlay) overlay.classList.toggle('is-visible', isOpen);
          toggle.setAttribute('aria-expanded', String(isOpen));
        }
      }

      overlay = e.target.closest(selectors.filterOverlay);
      if (overlay) closeFilterDrawer();
    });
  }

  function bindLoadMore() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest(selectors.loadMoreBtn);
      if (btn) {
        e.preventDefault();
        loadMoreProducts(btn.href, btn);
      }
    });
  }

  function bindPaginationClicks() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('#CollectionPagination a');
      if (link) {
        e.preventDefault();
        fetchAndRender(link.href);
        window.scrollTo({ top: document.querySelector(selectors.productsContainer).offsetTop - 100, behavior: 'smooth' });
      }
    });
  }

  /* ================================
     CORE: BUILD URL & FETCH
     ================================ */
  function buildUrl() {
    var form = document.querySelector(selectors.form);
    var params = [];

    if (form) {
      var inputs = form.querySelectorAll('input[data-filter-input]');
      inputs.forEach(function (input) {
        if (input.type === 'checkbox') {
          if (input.checked) {
            params.push(encodeURIComponent(input.name) + '=' + encodeURIComponent(input.value));
          }
        } else if (input.type === 'number' && input.value !== '') {
          params.push(encodeURIComponent(input.name) + '=' + encodeURIComponent(input.value));
        }
      });
    }

    var sortSelect = document.querySelector(selectors.sortSelect);
    if (sortSelect && sortSelect.value) {
      params.push('sort_by=' + encodeURIComponent(sortSelect.value));
    }

    var baseUrl = window.location.pathname;
    return baseUrl + (params.length > 0 ? '?' + params.join('&') : '');
  }

  function applyFilters() {
    var url = buildUrl();
    fetchAndRender(url);
  }

  function fetchAndRender(url) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    showLoading();

    var fetchUrl = url + (url.includes('?') ? '&' : '?') + 'section_id=' + SECTION_ID;

    fetch(fetchUrl, { signal: abortController.signal })
      .then(function (res) {
        if (!res.ok) throw new Error(res.statusText);
        return res.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        updateSection(parsed);
        history.replaceState(null, '', url);
        hideLoading();
        rebindEvents();
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        hideLoading();
      });
  }

  function loadMoreProducts(url, btn) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    btn.classList.add('is-loading');
    btn.setAttribute('disabled', 'true');

    var fetchUrl = url + (url.includes('?') ? '&' : '?') + 'section_id=' + SECTION_ID;

    fetch(fetchUrl, { signal: abortController.signal })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var newProducts = parsed.querySelector(selectors.productsContainer);
        var currentProducts = document.querySelector(selectors.productsContainer);

        if (newProducts && currentProducts) {
          var items = newProducts.querySelectorAll('.product-card');
          items.forEach(function (item) {
            currentProducts.appendChild(item.cloneNode(true));
          });
        }

        var newPagination = parsed.querySelector(selectors.paginationContainer);
        var currentPagination = document.querySelector(selectors.paginationContainer);
        if (currentPagination && newPagination) {
          currentPagination.innerHTML = newPagination.innerHTML;
        } else if (currentPagination) {
          currentPagination.innerHTML = '';
        }

        history.replaceState(null, '', url);
        rebindEvents();
      })
      .catch(function (err) {
        if (err.name !== 'AbortError') {
          btn.classList.remove('is-loading');
          btn.removeAttribute('disabled');
        }
      });
  }

  /* ================================
     DOM UPDATE
     ================================ */
  function updateSection(parsed) {
    replaceElement(selectors.productsContainer, parsed);
    replaceElement(selectors.paginationContainer, parsed);
    replaceElement(selectors.activeFilters, parsed);
    replaceElement(selectors.productCount, parsed);

    var newFilters = parsed.querySelector(selectors.filtersContainer);
    var currentFilters = document.querySelector(selectors.filtersContainer);
    if (newFilters && currentFilters) {
      currentFilters.innerHTML = newFilters.innerHTML;
    }

    var mobileFilters = document.querySelector('#FilterDrawer ' + selectors.filtersContainer);
    if (mobileFilters && newFilters) {
      mobileFilters.innerHTML = newFilters.innerHTML;
    }

    announceResults(parsed);
  }

  function replaceElement(selector, parsed) {
    var newEl = parsed.querySelector(selector);
    var currentEl = document.querySelector(selector);
    if (newEl && currentEl) {
      currentEl.innerHTML = newEl.innerHTML;
    } else if (currentEl && !newEl) {
      currentEl.innerHTML = '';
    }
  }

  function announceResults(parsed) {
    var countEl = parsed.querySelector(selectors.productCount);
    var liveRegion = document.querySelector('[data-filter-status]');
    if (liveRegion && countEl) {
      liveRegion.textContent = countEl.textContent;
    }
  }

  /* ================================
     LOADING STATE
     ================================ */
  function showLoading() {
    var container = document.querySelector(selectors.productsContainer);
    if (container) container.classList.add('is-loading');
  }

  function hideLoading() {
    var container = document.querySelector(selectors.productsContainer);
    if (container) container.classList.remove('is-loading');
  }

  /* ================================
     MOBILE DRAWER
     ================================ */
  function closeFilterDrawer() {
    var drawer = document.querySelector(selectors.filterDrawer);
    var overlay = document.querySelector(selectors.filterOverlay);
    var toggle = document.querySelector(selectors.filterToggle);
    if (drawer) drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-visible');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('no-scroll');
  }

  /* ================================
     REBIND AFTER AJAX
     ================================ */
  function rebindEvents() {
    bindFilterInputs();
  }

  /* ================================
     START
     ================================ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
